import { jsx as _jsx } from "react/jsx-runtime";
import { airportRowTitle, countryLabelFromCode, formatAirportOptionLabel, formatCityOptionLabel, isRailStationName, } from '@massilia/queries';
import { cn } from '@massilia/ui';
import { PlaneLanding, PlaneTakeoff, TrainFront } from 'lucide-react';
/** Code IATA à droite de la ligne : c'est ce que le client reconnaît d'un coup d'œil. */
const iataCodeBadge = (code, tone) => (_jsx("span", { className: cn('shrink-0 font-mono text-xs font-semibold tracking-widest tabular-nums', tone === 'city'
        ? 'bg-brand-50 text-brand-700 rounded-md px-2 py-1'
        : 'text-gray-light-500 px-1'), children: code }));
const modeIcon = (mode, className) => mode === 'departure' ? (_jsx(PlaneTakeoff, { className: className })) : (_jsx(PlaneLanding, { className: className }));
const cityOption = (cityGroup, mode) => {
    const country = countryLabelFromCode(cityGroup.countryCode);
    return {
        category: 'city',
        // Le pays seul : mentionner le regroupement d'aéroports ici embrouillait
        // plus qu'il n'informait. Le libellé stocké porte déjà l'information.
        description: country ?? undefined,
        icon: modeIcon(mode, 'h-4 w-4'),
        iconTone: 'brand',
        key: `city-${cityGroup.iataCityCode}`,
        label: formatCityOptionLabel(cityGroup.cityName, cityGroup.iataCityCode),
        trailing: iataCodeBadge(cityGroup.iataCityCode, 'city'),
        value: cityGroup.iataCityCode,
    };
};
/**
 * Recherche du site client : une ligne par ville, jamais d'aéroport.
 *
 * Chercher « orly » ou « CDG » continue de fonctionner — le serveur matche aussi
 * sur les noms et codes d'aéroports — mais renvoie la ville qui les contient.
 */
export const buildCityOptions = (response, mode) => response ? response.groupedByCitiesAirports.map((group) => cityOption(group, mode)) : [];
/**
 * Ville + ses aéroports en enfants. Réservé au back-office (règles origines /
 * destinations), où un agent doit pouvoir cibler un aéroport précis. La recherche
 * du site client passe par `buildCityOptions`.
 */
export const buildSearchOptions = (response, mode) => {
    if (!response)
        return [];
    const options = [];
    response.groupedByCitiesAirports.forEach((cityGroup) => {
        options.push(cityOption(cityGroup, mode));
        const airports = cityGroup.airports.filter((airport) => Boolean(airport.iataCode));
        airports.forEach((airport, index) => {
            options.push({
                category: 'airport',
                icon: isRailStationName(airport.name) ? (_jsx(TrainFront, { className: "text-gray-light-500 h-3.5 w-3.5" })) : (modeIcon(mode, 'text-gray-light-500 h-3.5 w-3.5')),
                isChild: true,
                isLastChild: index === airports.length - 1,
                key: `airport-${airport.iataCode}`,
                // Libellé stocké : auto-suffisant, il est réaffiché hors de la liste.
                label: formatAirportOptionLabel({
                    airportName: airport.name,
                    cityName: cityGroup.cityName,
                    iataCode: airport.iataCode,
                }),
                // Libellé affiché : la ville est déjà portée par la ligne parente.
                rowLabel: airportRowTitle(airport.name, airport.iataCode),
                trailing: iataCodeBadge(airport.iataCode, 'airport'),
                value: airport.iataCode,
            });
        });
    });
    return options;
};
